import React, { useState } from 'react';
import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';

function UpcomingPostsCalendar() {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [credits, setCredits] = useState(120); // TODO: 從 API 獲取真實點數

  // 生成當前週的日期（今天 + 未來4天）
  const getWeekDates = () => {
    const dates = [];
    const today = new Date();
    for (let i = 0; i < 5; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      dates.push(date);
    }
    return dates;
  };

  const weekDates = getWeekDates();

  const formatDay = (date) => {
    return date.toLocaleDateString('en-US', { weekday: 'short' });
  };

  const formatDate = (date) => {
    return date.getDate();
  };

  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isSameDay = (date1, date2) => {
    return date1.toDateString() === date2.toDateString();
  };

  return (
    <div
      className={`
        flex flex-col h-screen flex-shrink-0 transition-all duration-300 ease-in-out
        ${isCollapsed ? 'w-16' : 'w-[280px]'}
      `}
      style={{
        background: theme.bgSecondary,
        borderLeft: `1px solid ${theme.borderColor}`
      }}
    >
      {/* Credits Banner */}
      {!isCollapsed && (
        <div
          className="px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-gray-50 transition-colors"
          style={{
            borderBottom: `1px solid ${theme.borderColor}`
          }}
          onClick={() => navigate('/credits')}
        >
          <div className="flex items-center gap-2">
            <Icon icon="mdi:sparkles" className="text-base text-gray-400" />
            <span className="text-xs font-medium text-gray-600">升級以取得更多 AI 功能</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Icon icon="mdi:diamond" className={`text-base ${credits < 150 ? 'text-red-500' : 'text-blue-500'}`} />
            <span className={`text-sm font-bold ${credits < 150 ? 'text-red-500' : 'text-blue-500'}`}>
              {credits} 點
            </span>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="p-5 flex items-center justify-between" style={{ borderBottom: `1px solid ${theme.borderColor}` }}>
        {!isCollapsed && (
          <h2 className="text-sm font-medium" style={{ color: theme.textSecondary }}>Upcoming posts</h2>
        )}
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="w-8 h-8 flex items-center justify-center rounded-lg transition-colors"
          style={{
            color: theme.textSecondary,
            ':hover': { background: theme.bgHover }
          }}
          onMouseEnter={(e) => e.currentTarget.style.background = theme.bgHover}
          onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
        >
          <Icon
            icon={isCollapsed ? 'mdi:chevron-left' : 'mdi:chevron-right'}
            className="text-lg"
          />
        </button>
      </div>

      {/* Content - 只在展開時顯示 */}
      {!isCollapsed && (
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Calendar Icon Link */}
          <div className="flex items-center justify-end px-5 pt-5 pb-3">
            <button
              onClick={() => navigate('/calendar/week')}
              className="w-8 h-8 flex items-center justify-center rounded-lg transition-colors"
              title="Go to calendar"
              style={{ color: theme.textSecondary }}
              onMouseEnter={(e) => e.currentTarget.style.background = theme.bgHover}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
            >
              <Icon icon="mdi:calendar-month" width={18} />
            </button>
          </div>

          {/* Week Date Selector */}
          <div className="flex justify-between gap-1 px-5 pb-4">
            {weekDates.map((date, index) => (
              <button
                key={index}
                onClick={() => setSelectedDate(date)}
                className="flex flex-col items-center gap-1 px-2 py-2 rounded-lg transition-all cursor-pointer"
                style={{
                  background: isSameDay(date, selectedDate) ? theme.bgHover : 'transparent',
                  color: isSameDay(date, selectedDate) ? theme.textPrimary : theme.textSecondary,
                  border: isToday(date) ? `1px solid ${theme.borderColor}` : '1px solid transparent'
                }}
                onMouseEnter={(e) => {
                  if (!isSameDay(date, selectedDate)) {
                    e.currentTarget.style.background = theme.bgHover;
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSameDay(date, selectedDate)) {
                    e.currentTarget.style.background = 'transparent';
                  }
                }}
              >
                <span className="text-[11px] font-medium">
                  {formatDay(date)}
                </span>
                <span className="text-sm font-semibold">
                  {formatDate(date)}
                </span>
              </button>
            ))}
          </div>

          {/* Posts List or Empty State - 填滿剩餘空間 */}
          <div className="flex-1 flex flex-col items-center justify-center px-5 pb-5" style={{ background: '#FAFAFA' }}>
            {/* Empty State */}
            <div className="flex flex-col items-center justify-center">
              <div className="relative mb-4">
                {/* Illustration */}
                <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ background: '#F5F5F5' }}>
                  <Icon icon="mdi:calendar-blank-outline" className="text-3xl" style={{ color: theme.textSecondary }} />
                </div>
                <div className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full flex items-center justify-center" style={{ background: theme.bgSecondary, border: '2px solid #FAFAFA' }}>
                  <Icon icon="mdi:plus" className="text-sm" style={{ color: theme.textSecondary }} />
                </div>
              </div>

              <div className="text-center mb-5">
                <p className="text-xs mb-0.5" style={{ color: theme.textSecondary }}>
                  Looks like you don't have
                </p>
                <p className="text-xs" style={{ color: theme.textSecondary }}>
                  any scheduled posts...
                </p>
              </div>

              <button
                onClick={() => navigate('/posts')}
                className="px-3 py-2 text-xs font-medium rounded-lg transition-opacity hover:opacity-80"
                style={{
                  background: theme.buttonSecondary,
                  color: theme.textPrimary
                }}
              >
                Create a new post
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Collapsed State - Icon Only */}
      {isCollapsed && (
        <div className="flex-1 flex flex-col items-center gap-4">
          {/* Credits Display - Collapsed */}
          <div
            className="w-full py-3 flex flex-col items-center gap-1 cursor-pointer hover:bg-gray-50 transition-colors"
            style={{ borderBottom: `1px solid ${theme.borderColor}` }}
            onClick={() => navigate('/credits')}
          >
            <Icon icon="mdi:diamond" className={`text-lg ${credits < 150 ? 'text-red-500' : 'text-blue-500'}`} />
            <span className={`text-xs font-bold ${credits < 150 ? 'text-red-500' : 'text-blue-500'}`}>
              {credits}
            </span>
          </div>

          <button
            onClick={() => setIsCollapsed(false)}
            className="w-10 h-10 flex items-center justify-center rounded-lg transition-colors mt-2"
            title="Expand calendar"
            style={{ color: theme.textSecondary }}
            onMouseEnter={(e) => e.currentTarget.style.background = theme.bgHover}
            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
          >
            <Icon icon="mdi:calendar-month-outline" className="text-lg" />
          </button>
        </div>
      )}
    </div>
  );
}

export default UpcomingPostsCalendar;
